import React from 'react';
import { MapPin, Clock, Phone, Navigation } from 'lucide-react';
import SEO from '../components/SEO';
import contactData from '../data/contact.json';

const Location = () => {
  return (
    <div className="pt-20">
      <SEO 
        title="Standort & Öffnungszeiten - Ali Baba Kebab Sarnen | Grundacherweg 1"
        description="Besuchen Sie uns: Grundacherweg 1, 6060 Sarnen. Öffnungszeiten: Mo-Fr 10:00-22:00, Sa-So 11:00-23:00. Kostenlose Parkplätze. Rollstuhlgerecht. ☎️ 041 660 29 29"
        keywords="Kebab Sarnen Adresse, Restaurant Standort Sarnen, Öffnungszeiten Sarnen, Parkplätze Sarnen"
        path="/location"
      />
      
      {/* Hero */}
      <section className="section bg-[#1C1917] text-center">
        <div className="container mx-auto">
          <h1 className="text-6xl md:text-7xl text-white mb-6">STANDORT & ÖFFNUNGSZEITEN</h1>
          <p className="text-xl text-[#F7E39A] max-w-3xl mx-auto">
            Besuchen Sie uns in Sarnen – wir freuen uns auf Ihren Besuch
          </p>
        </div>
      </section>

      {/* Map & Info */}
      <section className="section bg-[#0F0E0D]">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Google Maps Embed */}
            <div className="rounded-lg overflow-hidden" style={{ border: '2px solid rgba(247, 227, 154, 0.3)' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2717.8!2d8.2457!3d46.8960!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478ff2e7c8c8c8c8%3A0x1234567890abcdef!2sGrundacherweg%201%2C%206060%20Sarnen%2C%20Switzerland!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ali Baba Kebab & Vegie Location"
              ></iframe>
            </div>

            {/* Info Cards */}
            <div className="space-y-6">
              {/* Address */}
              <div className="bg-[#1C1917] p-8 rounded-lg" style={{ border: '1px solid rgba(247, 227, 154, 0.3)' }}>
                <div className="flex items-start space-x-4">
                  <MapPin className="text-[#F7E39A] flex-shrink-0" size={32} />
                  <div>
                    <h3 className="text-2xl text-[#F7E39A] mb-3">Adresse</h3>
                    <p className="text-white text-lg">{contactData.restaurant.address.street}</p>
                    <p className="text-white text-lg mb-4">{contactData.restaurant.address.zip} {contactData.restaurant.address.city}, {contactData.restaurant.address.country}</p>
                    <a
                      href={contactData.social.google}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-[#F7E39A] hover:text-white transition-colors"
                    >
                      <Navigation size={18} />
                      <span>Route planen</span>
                    </a>
                  </div>
                </div>
              </div>

              {/* Opening Hours */}
              <div className="bg-[#1C1917] p-8 rounded-lg" style={{ border: '1px solid rgba(247, 227, 154, 0.3)' }}>
                <div className="flex items-start space-x-4">
                  <Clock className="text-[#F7E39A] flex-shrink-0" size={32} />
                  <div className="w-full">
                    <h3 className="text-2xl text-[#F7E39A] mb-4">Öffnungszeiten</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white font-medium">{contactData.hours.weekdays.days}</p>
                        <p className="text-[#E3E3E3]">{contactData.hours.weekdays.lunch.open} - {contactData.hours.weekdays.lunch.close}</p>
                        <p className="text-[#E3E3E3]">{contactData.hours.weekdays.dinner.open} - {contactData.hours.weekdays.dinner.close}</p>
                      </div>
                      <div>
                        <p className="text-white font-medium">{contactData.hours.weekend.days}</p>
                        <p className="text-[#E3E3E3]">{contactData.hours.weekend.open} - {contactData.hours.weekend.close}</p>
                      </div>
                    </div>
                    <div className="mt-4 p-4 bg-[#A7C957]/10 rounded-lg">
                      <p className="text-[#A7C957] text-sm">
                        Durchgehend warme Küche – keine Mittagspause
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-[#F7E39A] p-8 rounded-lg text-[#1C1917]">
                <div className="flex items-start space-x-4">
                  <Phone className="flex-shrink-0" size={32} />
                  <div>
                    <h3 className="text-2xl font-bold mb-3">Telefonbestellung</h3>
                    <p className="text-lg mb-4">Rufen Sie uns an und bestellen Sie vor – wir bereiten alles frisch für Sie zu!</p>
                    <a
                      href={`tel:${contactData.restaurant.phone}`}
                      className="text-3xl font-bold hover:opacity-80 transition-opacity"
                    >
                      {contactData.restaurant.phoneDisplay}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Parking Info */}
          <div className="mt-12 bg-[#1C1917] p-8 rounded-lg text-center" style={{ border: '1px solid rgba(247, 227, 154, 0.3)' }}>
            <h3 className="text-2xl text-[#F7E39A] mb-4">Parkmöglichkeiten</h3>
            <p className="text-[#E3E3E3] text-lg">
              Kostenlose Parkplätze direkt vor dem Restaurant verfügbar. Rollstuhlgerechter Zugang vorhanden.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Location;
